import { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Tabs, TabsContent } from "@component/tabs";
import { Textarea } from "@component/textarea";
import { CopyIcon } from "lucide-react";
import { generateUserReferral, getUserReferral } from "@service/referral";
import { useUserProfileContext } from "@context/user_profile";
import { getSubdomainHost } from "@util/name";

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export default function InviteFriendDialog(props: Props) {
    const [, currentUserInfo] = useUserProfileContext();
    const [activeTab, setActiveTab] = useState("link");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState(
        "Hey! I thought you might be interested in joining ourteam. They have great job opportunities and a referral program. Check it out using my referral link!"
    );
    const [isLoading, setLoading] = useState<boolean>(false);
    const [referralCode, setReferralCode] = useState<string>("");

    useEffect(() => {
        if (props.isOpen) {
            setLoading(true);
            getUserReferral().then((response) => {
                if (response.data.data && response.data.data.referral_code) {
                    setReferralCode(response.data.data.referral_code || "");
                    setLoading(false);
                } else {
                    return generateUserReferral().then((response) => {
                        if (response && response.data.code === 200) {
                            setReferralCode(response.data.data.referral_code);
                            setLoading(false);
                        }
                    })
                }
            })
        }
    }, [props.isOpen])

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`${getSubdomainHost(currentUserInfo?.company_subdomain || "")}?referral=${referralCode}`);
    };

    const handleSendEmail = () => {
        // In a real app, this would send the email
        setEmail("");
        props.onClose();
    };

    return (
        <Dialog open={props.isOpen} onOpenChange={props.onClose}>
            <DialogContent className="sm:max-w-[500px] rounded-xl border border-gray-200">
                <DialogHeader>
                    <DialogTitle>Invite Friends</DialogTitle>
                    <DialogDescription>
                        Share your referral link with friends to earn rewards when they get
                        hired.
                    </DialogDescription>
                </DialogHeader>

                <Tabs
                    defaultValue="link"
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    {/* <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="link" className="flex items-center">
                            <LinkIcon className="h-4 w-4 mr-2" />
                            Share Link
                        </TabsTrigger>
                        <TabsTrigger value="email" className="flex items-center">
                            <MailIcon className="h-4 w-4 mr-2" />
                            Email Invite
                        </TabsTrigger>
                    </TabsList> */}

                    <TabsContent value="link" className="space-y-4 py-0">
                        <div className="space-y-2">
                            <Label htmlFor="referral-link">Your referral link</Label>
                            {isLoading ? (
                                <div className="flex flex-row items-center justify-center w-full">
                                    <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            ) : (
                                <>
                                    {referralCode !== "" && (
                                        <div className="flex items-center space-x-2">
                                            <Input
                                                id="referral-link"
                                                value={`${import.meta.env.VITE_TALENT_AI_APP}?referral=${referralCode}`}
                                                readOnly
                                                className="flex-1"
                                            />

                                            <Button
                                                type="button"
                                                size="icon"
                                                variant="outline"
                                                onClick={handleCopyLink}
                                            >
                                                <CopyIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}

                        </div>
                    </TabsContent>

                    <TabsContent value="email" className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email address</Label>
                            <Input
                                id="email"
                                placeholder="friend@example.com"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="message">Message</Label>
                            <Textarea
                                id="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={4}
                            />
                        </div>
                    </TabsContent>
                </Tabs>

                {activeTab === "email" && (
                    <DialogFooter>
                        <div className="flex flex-row space-x-2">
                            <Button variant="outline" onClick={props.onClose}>
                                Cancel
                            </Button>
                            <Button onClick={handleSendEmail} disabled={!email}>
                                Send Invitation
                            </Button>
                        </div>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
